import React, { useRef } from "react";
import { useState, useEffect } from "react";
import LoadingFullScreen from "../../components/LoadingFullScreen";
import "./Messaging.scss";
import ConversationViewer from "./components/ConversationViewer";
import MessageList from "./components/MessageList";
import { ConversationPreview } from "./types";
import { Page, RootState } from "../types";
import { get } from "../../utils/fetch";
import { useSelector, useDispatch } from "react-redux";
import { PubNubState } from "../pubnub/reducer";
import { User } from "../session/types";
import { Organization } from "../organizations/types";
import { BrowserView, MobileView } from "react-device-detect";
import history from "../../history";
import { setMobileMessagingScrollTop } from "./actions";
import { MassNotification } from "./components/MassNotification";

const MessagingPage = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pubNubKey, setPubNubKey] = useState<string>();
  const [conversationNextUrl, setConversationNextUrl] = useState<string>();
  const [selectedConversation, setSelectedConversation] = useState<ConversationPreview | null>(
    null
  );
  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const newPubNubObject: PubNubState = useSelector((state: RootState) => state.pubnub.message);
  const currentUser: User | null = useSelector(
    (state: RootState) => state.session.data.currentUser
  );
  const currentOrg: Organization | null = useSelector(
    (state: RootState) => state.orgs.currentOrganization
  );
  const scrollLimitApiConst = 60;
  const pageSize = 25;
  const dispatch = useDispatch();
  const [scrollPosition, setSrollPosition] = useState(0);
  const desktopMessageList = useRef<HTMLDivElement>(null);
  const [nextUrlMutex, setNextUrlMutex] = useState<boolean>(false);
  const mobileMessageList = useRef<HTMLDivElement>(null);
  const mobileMessageListScrollTop: number = useSelector(
    (state: RootState) => state.messaging.scrollTop
  );
  const [massMessagingMode, setMassMessagingMode] = useState<boolean>(false);

  const handleScroll = () => {
    if (desktopMessageList?.current) {
      //Grabbing the percent of the user scrolling. 100 = scrolled to the top
      const scrollTopPercent = Math.round(
        (desktopMessageList.current.scrollTop /
          (desktopMessageList.current.scrollHeight - desktopMessageList.current.clientHeight)) *
          100
      );
      setSrollPosition(scrollTopPercent);
    }
    if (mobileMessageList?.current) {
      //Grabbing the percent of the user scrolling. 100 = scrolled to the top
      const scrollTopPercent = Math.round(
        (mobileMessageList.current.scrollTop /
          (mobileMessageList.current.scrollHeight - mobileMessageList.current.clientHeight)) *
          100
      );
      setSrollPosition(scrollTopPercent);
    }
  };

  const getConversations = async (nextUrl = "") => {
    let url = `/api/conversations/?page=1&page_size=${pageSize}`;
    if (nextUrl) {
      url = nextUrl;
    } else {
      setLoading(true);
    }
    const resp = await get<Page<ConversationPreview>>(url);
    setConversationNextUrl("");
    if (resp.data) {
      if (resp.data.next) {
        setNextUrlMutex(false);
        setConversationNextUrl(resp.data.next);
      }
      if (conversations) {
        setConversations(conversations.concat(resp.data.results));
      } else {
        setConversations(resp.data.results);
      }

      if (!nextUrl && mobileMessageList && mobileMessageList.current) {
        mobileMessageList.current.scrollTop = mobileMessageListScrollTop;
        dispatch(setMobileMessagingScrollTop(0));
      }
    }
    if (!nextUrl) {
      setLoading(false);
    }
  };

  const createPubNubKey = () => {
    if (!currentUser || !currentOrg) {
      return;
    }
    setPubNubKey(`${currentOrg?.id}.${currentUser?.id}`);
  };

  const getNewPreview = async (id: string) => {
    if (!id || !pubNubKey) {
      return;
    }
    const url = `/api/conversations/${id}/preview/`;
    const resp = await get<ConversationPreview>(url);
    if (resp.data) {
      if (newPubNubObject.byId[pubNubKey].conversationEnd) {
        // Update already existing conversation id
        const index = conversations.findIndex(c => c.id === resp.data?.id);
        if (index) {
          const oldConversations = [...conversations];
          oldConversations[index].readOnly = true;
          setConversations([...oldConversations]);
        }
      } else {
        let newConversations = [resp.data];
        newConversations = newConversations.concat(
          conversations.filter(c => c.id !== resp.data.id)
        );
        setConversations(newConversations);
      }
    }
  };
  useEffect(() => {
    createPubNubKey();
  }, [currentUser, currentOrg]);

  useEffect(() => {
    if (
      !newPubNubObject ||
      !pubNubKey ||
      (pubNubKey && !newPubNubObject.byId[pubNubKey]) ||
      !newPubNubObject.byId[pubNubKey].newestConversationId
    )
      return;
    getNewPreview(newPubNubObject.byId[pubNubKey].newestConversationId);
    if (newPubNubObject.byId[pubNubKey].newMessage) {
      // getMessage();
    }
  }, [newPubNubObject, pubNubKey]);

  useEffect(() => {
    getConversations();
  }, [currentUser]);

  useEffect(() => {
    if (desktopMessageList.current) {
      desktopMessageList.current.addEventListener("scroll", handleScroll, { passive: true });
    }
    if (mobileMessageList.current) {
      mobileMessageList.current.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      if (desktopMessageList.current) {
        desktopMessageList.current.removeEventListener("scroll", handleScroll);
      }
      if (mobileMessageList.current) {
        mobileMessageList.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (scrollPosition >= scrollLimitApiConst && conversationNextUrl && !nextUrlMutex) {
      setNextUrlMutex(true);
      getConversations(conversationNextUrl);
    }
  }, [scrollPosition]);

  return (
    <>
      {loading && <LoadingFullScreen />}
      <BrowserView>
        <div className="messaging-container width-restrict">
          <div className="page-header mt-10">
            <div className="title ml-10">My Inbox</div>
            {currentUser?.featureFlags?.enableBulkSms && !currentOrg?.smsCommunicationDisabled && (
              <button
                onClick={() => {
                  setMassMessagingMode(true);
                  setSelectedConversation(null);
                }}
              >
                <span>
                  <i className="far fa-bell-on mr-10"></i>New Mass Notification
                </span>
              </button>
            )}
          </div>
          <div className="flex flex-1 overflow-hidden mb-10">
            <div className="list flex-1">
              <div className="scroll" ref={desktopMessageList}>
                {conversations &&
                  conversations.map((conversation, idx) => (
                    <div
                      key={idx}
                      className={
                        selectedConversation && selectedConversation.id === conversation.id
                          ? "selected "
                          : ""
                      }
                      onClick={() => {
                        conversation.unread = false;
                        setSelectedConversation(conversation);
                        setMassMessagingMode(false);
                      }}
                    >
                      <MessageList conversationPreview={conversation}></MessageList>
                    </div>
                  ))}
              </div>
            </div>
            <div className="flex-2 ml-15">
              {!selectedConversation && !massMessagingMode && (
                <div className="select-conv">
                  <div>Select a conversation at left to read</div>
                  <div>
                    <i className="fas fa-envelope icon"></i>
                  </div>
                </div>
              )}

              {selectedConversation && !massMessagingMode && (
                <ConversationViewer
                  conversationId={selectedConversation.id}
                  setLoading={setLoading}
                />
              )}
              {massMessagingMode && (
                <MassNotification
                  setLoading={setLoading}
                  closeMassNotification={() => setMassMessagingMode(false)}
                />
              )}
            </div>
          </div>
        </div>
      </BrowserView>
      <MobileView>
        <div className="messaging-container-mobile small-font fade">
          <div className="scroll" ref={mobileMessageList}>
            {conversations && conversations.length > 0 && (
              <h1 className="mt-10 pl-10 pr-10">My Inbox</h1>
            )}
            {conversations &&
              conversations.map((conversation, idx) => (
                <div
                  key={idx}
                  className={
                    selectedConversation && selectedConversation.id === conversation.id
                      ? "selected "
                      : ""
                  }
                  onClick={() => {
                    conversation.unread = false;
                    setSelectedConversation(conversation);
                    dispatch(setMobileMessagingScrollTop(mobileMessageList.current.scrollTop));
                    history.push(`/messaging/${conversation.id}/`);
                  }}
                >
                  <MessageList conversationPreview={conversation}></MessageList>
                </div>
              ))}
          </div>
        </div>
      </MobileView>
    </>
  );
};

export default MessagingPage;
